import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "@/lib/api";
import { branchSlug, publicPath } from "@/lib/branch";
import { announcementSchema, salonProfileSchema } from "../schemas/salon.schemas";

export const salonKeys = {
  all: ["salon"] as const,
  profile: () => [...salonKeys.all, "profile", branchSlug ?? "salon"] as const,
  announcements: () => [...salonKeys.all, "announcements"] as const,
};

/**
 * The salon itself. Anonymous, and the first thing the app asks for: until it answers there is
 * nothing to paint but a spinner, because the name, the colours and the logo all come from it.
 *
 * Not refetched on every navigation, but re-read when the customer comes back to the tab: the profile
 * carries the salon's colour theme and light or dark (FR-SA-189, FR-SA-204), which a salon admin
 * changes in the staff app and expects to see on an open booking page by switching back to it. A few
 * seconds of freshness keep a burst of mounts to one request; the API's public cache is dropped the
 * moment such a setting is saved (SettingChangedEvent), so the re-read gets the new value.
 */
export const salonProfileQuery = () =>
  queryOptions({
    queryKey: salonKeys.profile(),
    // The branch's profile for a branch link, the salon's otherwise (FR-SA-244).
    queryFn: () => apiClient.get(salonProfileSchema, publicPath("")),
    retry: false,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });

export const announcementsQuery = () =>
  queryOptions({
    queryKey: salonKeys.announcements(),
    queryFn: () => apiClient.get(z.array(announcementSchema), "/api/v1/salon/public/announcements"),
    staleTime: 5 * 60_000,
  });

/**
 * The id of the branch this page is for, from the profile already read, for a booking or a waitlist
 * place (FR-SA-246). Undefined before the profile has answered, and from an older server; the API then
 * books the salon's only branch.
 */
export function profileBranchId(queryClient: QueryClient): string | undefined {
  return queryClient.getQueryData(salonProfileQuery().queryKey)?.branch?.id;
}
